import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { createRazorpayOrder, verifyPayment } from '../../utils/razorpayService';
import './QRCheckout.css';

const QRCheckout = () => {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [table, setTable] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [processing, setProcessing] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');

  useEffect(() => {
    loadCart();
    fetchTableInfo();
    loadCustomerInfo();
  }, [tableId]);

  const loadCart = () => {
    const savedCart = localStorage.getItem(`qr-cart-${tableId}`);
    const instructions = localStorage.getItem(`qr-special-instructions-${tableId}`) || '';
    
    if (savedCart) {
      setCart(JSON.parse(savedCart));
      setSpecialInstructions(instructions);
    } else {
      navigate(`/qr/table/${tableId}`);
    }
  };

  const fetchTableInfo = async () => {
    try {
      const tableDoc = await getDoc(doc(db, 'tables', tableId));
      if (tableDoc.exists()) {
        setTable({ id: tableDoc.id, ...tableDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching table:', error);
    }
  };

  const loadCustomerInfo = () => {
    const savedInfo = localStorage.getItem('qr-customer-info');
    if (savedInfo) {
      setCustomerInfo(JSON.parse(savedInfo));
    }
  };

  const saveCustomerInfo = () => {
    localStorage.setItem('qr-customer-info', JSON.stringify(customerInfo));
  };

  const getSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTax = () => {
    return getSubtotal() * 0.05;
  };

  const getTotal = () => {
    return getSubtotal() + getTax();
  };

  const createOrder = async () => {
    const orderData = {
      orderType: 'dine-in',
      tableNumber: table.tableNumber,
      tableId: tableId,
      customerName: customerInfo.name,
      customerPhone: customerInfo.phone,
      customerEmail: customerInfo.email,
      items: cart.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        isVeg: item.isVeg
      })),
      subtotal: getSubtotal(),
      tax: getTax(),
      total: getTotal(),
      paymentMethod: paymentMethod,
      paymentStatus: paymentMethod === 'cash' ? 'pending' : 'pending',
      status: 'pending',
      specialInstructions: specialInstructions,
      timestamp: serverTimestamp(),
      source: 'qr-ordering'
    };

    const orderRef = await addDoc(collection(db, 'orders'), orderData);
    return orderRef.id;
  };

  const handlePlaceOrder = async () => {
    // Validation
    if (!customerInfo.name.trim() || !customerInfo.phone.trim()) {
      alert('Please fill in your name and phone number');
      return;
    }

    if (customerInfo.phone.length < 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    setProcessing(true);
    saveCustomerInfo();

    try {
      if (paymentMethod === 'cash') {
        // Cash payment - create order directly
        const orderId = await createOrder();
        
        // Clear cart
        localStorage.removeItem(`qr-cart-${tableId}`);
        localStorage.removeItem(`qr-special-instructions-${tableId}`);
        
        navigate(`/qr/confirmation/${orderId}`);
      } else {
        // Online payment - Razorpay
        const orderId = await createOrder();
        const totalAmount = getTotal();

        const razorpayOrder = await createRazorpayOrder(totalAmount, {
          orderId,
          customerName: customerInfo.name,
          customerPhone: customerInfo.phone
        });

        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: 'Odisha Zaika',
          description: `Table ${table.tableNumber} Order`,
          order_id: razorpayOrder.id,
          prefill: {
            name: customerInfo.name,
            contact: customerInfo.phone,
            email: customerInfo.email
          },
          theme: {
            color: '#667eea'
          },
          handler: async function(response) {
            try {
              const verification = await verifyPayment({
                orderId,
                paymentId: response.razorpay_payment_id,
                razorpayOrderId: response.razorpay_order_id,
                signature: response.razorpay_signature
              });

              if (verification.verified) {
                localStorage.removeItem(`qr-cart-${tableId}`);
                localStorage.removeItem(`qr-special-instructions-${tableId}`);
                navigate(`/qr/confirmation/${orderId}`);
              } else {
                alert('Payment verification failed. Please contact staff.');
              }
            } catch (error) {
              console.error('Payment verification error:', error);
              alert('Payment verification failed. Please contact staff.');
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
        setProcessing(false);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
      setProcessing(false);
    }
  };

  if (!table || cart.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
      </div>
    );
  }

  return (
    <div className="qr-checkout-page">
      <div className="checkout-header">
        <button className="back-btn" onClick={() => navigate(`/qr/cart/${tableId}`)}>
          ← Back to Cart
        </button>
        <h1>Checkout</h1>
      </div>

      <div className="checkout-content">
        <div className="section customer-info-section">
          <h3>Customer Information</h3>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              placeholder="Your name"
              value={customerInfo.name}
              onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label>Phone Number *</label>
            <input
              type="tel"
              placeholder="10-digit mobile number"
              value={customerInfo.phone}
              onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
              maxLength="10"
            />
          </div>
          <div className="form-group">
            <label>Email (Optional)</label>
            <input
              type="email"
              placeholder="your@email.com"
              value={customerInfo.email}
              onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
            />
          </div>
        </div>

        <div className="section payment-method-section">
          <h3>Payment Method</h3>
          <div className="payment-options">
            <label className={`payment-option ${paymentMethod === 'upi' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="payment"
                value="upi"
                checked={paymentMethod === 'upi'}
                onChange={(e) => setPaymentMethod(e.target.value)}
              />
              <div className="option-content">
                <span className="option-icon">📱</span>
                <div>
                  <strong>UPI</strong>
                  <p>Google Pay, PhonePe, Paytm</p>
                </div>
              </div>
            </label>

            <label className={`payment-option ${paymentMethod === 'card' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="payment"
                value="card"
                checked={paymentMethod === 'card'}
                onChange={(e) => setPaymentMethod(e.target.value)}
              />
              <div className="option-content">
                <span className="option-icon">💳</span>
                <div>
                  <strong>Card</strong>
                  <p>Debit / Credit Card</p>
                </div>
              </div>
            </label>

            <label className={`payment-option ${paymentMethod === 'cash' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="payment"
                value="cash"
                checked={paymentMethod === 'cash'}
                onChange={(e) => setPaymentMethod(e.target.value)}
              />
              <div className="option-content">
                <span className="option-icon">💵</span>
                <div>
                  <strong>Cash</strong>
                  <p>Pay when bill arrives</p>
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="section order-summary-section">
          <h3>Order Summary</h3>
          <div className="summary-items">
            {cart.map(item => (
              <div key={item.id} className="summary-item">
                <span>{item.quantity}x {item.name}</span>
                <span>₹{item.price * item.quantity}</span>
              </div>
            ))}
          </div>
          <div className="summary-divider"></div>
          <div className="summary-row">
            <span>Subtotal</span>
            <span>₹{getSubtotal().toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>GST (5%)</span>
            <span>₹{getTax().toFixed(2)}</span>
          </div>
          <div className="summary-divider"></div>
          <div className="summary-row total-row">
            <span>Total</span>
            <span>₹{getTotal().toFixed(2)}</span>
          </div>
        </div>

        <button 
          className="btn-place-order"
          onClick={handlePlaceOrder}
          disabled={processing}
        >
          {processing ? (
            <>
              <span className="spinner"></span> Processing...
            </>
          ) : (
            `Place Order • ₹${getTotal().toFixed(2)}`
          )}
        </button>

        <p className="terms-note">
          By placing this order, you agree to our <a href="/terms">Terms & Conditions</a>
        </p>
      </div>
    </div>
  );
};

export default QRCheckout;
